/**
 * Word Clash Game Engine
 *
 * Implements the core game logic for Word Clash (Scrabble-inspired crossword word game).
 * Manages board state, tile bag, scoring, word validation, and game flow.
 */

import {
  Tile,
  BoardCell,
  PremiumSquareType,
  WordGamePlayer,
  WordGameState,
  WordPlacement,
  TilePlacement,
  PlacedWord,
  Direction,
  MoveHistoryEntry,
  WordGamePhase,
} from './types';
import { isValidWord } from './dictionary';

// ============================================================================
// Constants
// ============================================================================

const BOARD_SIZE = 15;
const RACK_SIZE = 7;
const BINGO_BONUS = 50;

/** Standard letter distribution: [letter, value, count] */
const TILE_DISTRIBUTION: [string, number, number][] = [
  ['A', 1, 9], ['B', 3, 2], ['C', 3, 2], ['D', 2, 4], ['E', 1, 12],
  ['F', 4, 2], ['G', 2, 3], ['H', 4, 2], ['I', 1, 9], ['J', 8, 1],
  ['K', 5, 1], ['L', 1, 4], ['M', 3, 2], ['N', 1, 6], ['O', 1, 8],
  ['P', 3, 2], ['Q', 10, 1], ['R', 1, 6], ['S', 1, 4], ['T', 1, 6],
  ['U', 1, 4], ['V', 4, 2], ['W', 4, 2], ['X', 8, 1], ['Y', 4, 2],
  ['Z', 10, 1], ['', 0, 2], // Blank tiles
];

/** Premium square positions (standard Scrabble layout) */
const TRIPLE_WORD_POSITIONS: [number, number][] = [
  [0, 0], [0, 7], [0, 14], [7, 0], [7, 14], [14, 0], [14, 7], [14, 14],
];

const DOUBLE_WORD_POSITIONS: [number, number][] = [
  [1, 1], [2, 2], [3, 3], [4, 4], [10, 4], [11, 3], [12, 2], [13, 1],
  [1, 13], [2, 12], [3, 11], [4, 10], [10, 10], [11, 11], [12, 12], [13, 13],
];

const TRIPLE_LETTER_POSITIONS: [number, number][] = [
  [1, 5], [1, 9], [5, 1], [5, 5], [5, 9], [5, 13],
  [9, 1], [9, 5], [9, 9], [9, 13], [13, 5], [13, 9],
];

const DOUBLE_LETTER_POSITIONS: [number, number][] = [
  [0, 3], [0, 11], [2, 6], [2, 8], [3, 0], [3, 7], [3, 14],
  [6, 2], [6, 6], [6, 8], [6, 12], [7, 3], [7, 11],
  [8, 2], [8, 6], [8, 8], [8, 12], [11, 0], [11, 7], [11, 14],
  [12, 6], [12, 8], [14, 3], [14, 11],
];

// ============================================================================
// Engine Class
// ============================================================================

export class WordGameEngine {
  private state: WordGameState;
  private tileIdCounter: number = 0;

  constructor() {
    this.state = this.createInitialState();
  }

  // ==========================================================================
  // Initialization
  // ==========================================================================

  private createInitialState(): WordGameState {
    return {
      board: this.createBoard(),
      players: [],
      currentPlayerIndex: 0,
      tileBag: this.createTileBag(),
      phase: 'waiting',
      turnNumber: 0,
      lastPlayedWord: null,
      lastPlacedPositions: [],
      moveHistory: [],
      winner: null,
      gameOverReason: null,
    };
  }

  private createBoard(): BoardCell[][] {
    const board: BoardCell[][] = [];
    for (let row = 0; row < BOARD_SIZE; row++) {
      board[row] = [];
      for (let col = 0; col < BOARD_SIZE; col++) {
        board[row][col] = {
          tile: null,
          premium: this.getPremiumType(row, col),
          row,
          col,
        };
      }
    }
    return board;
  }

  private getPremiumType(row: number, col: number): PremiumSquareType {
    if (row === 7 && col === 7) return PremiumSquareType.CENTER;
    if (TRIPLE_WORD_POSITIONS.some(([r, c]) => r === row && c === col)) return PremiumSquareType.TRIPLE_WORD;
    if (DOUBLE_WORD_POSITIONS.some(([r, c]) => r === row && c === col)) return PremiumSquareType.DOUBLE_WORD;
    if (TRIPLE_LETTER_POSITIONS.some(([r, c]) => r === row && c === col)) return PremiumSquareType.TRIPLE_LETTER;
    if (DOUBLE_LETTER_POSITIONS.some(([r, c]) => r === row && c === col)) return PremiumSquareType.DOUBLE_LETTER;
    return PremiumSquareType.NONE;
  }

  private createTileBag(): Tile[] {
    const bag: Tile[] = [];
    for (const [letter, value, count] of TILE_DISTRIBUTION) {
      for (let i = 0; i < count; i++) {
        bag.push({
          letter,
          value,
          isBlank: letter === '',
          id: `tile-${this.tileIdCounter++}`,
        });
      }
    }
    return this.shuffleArray(bag);
  }

  private shuffleArray<T>(array: T[]): T[] {
    const arr = [...array];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  // ==========================================================================
  // Game Setup
  // ==========================================================================

  startGame(playerNames: string[], aiFlags: boolean[]): void {
    this.state = this.createInitialState();
    this.state.players = playerNames.map((name, idx) => ({
      id: `player-${idx}`,
      name,
      score: 0,
      rack: [],
      isAI: aiFlags[idx] || false,
      consecutivePasses: 0,
    }));

    // Deal tiles to each player
    for (const player of this.state.players) {
      this.fillRack(player);
    }

    this.state.phase = 'playing';
    this.state.turnNumber = 1;
  }

  private fillRack(player: WordGamePlayer): void {
    while (player.rack.length < RACK_SIZE && this.state.tileBag.length > 0) {
      const tile = this.state.tileBag.pop()!;
      player.rack.push(tile);
    }
  }

  // ==========================================================================
  // Move Validation & Execution
  // ==========================================================================

  /**
   * Validate and execute a word placement.
   * Returns the score if successful, or an error message.
   */
  playWord(playerIndex: number, placements: TilePlacement[]): { success: boolean; score?: number; words?: PlacedWord[]; error?: string } {
    if (this.state.phase !== 'playing') {
      return { success: false, error: 'Game is not in progress' };
    }
    if (playerIndex !== this.state.currentPlayerIndex) {
      return { success: false, error: 'Not your turn' };
    }
    if (placements.length === 0) {
      return { success: false, error: 'No tiles placed' };
    }

    // Validate placements are in a line
    const direction = this.getPlacementDirection(placements);
    if (!direction) {
      return { success: false, error: 'Tiles must be placed in a straight line' };
    }

    // Validate tiles are on empty cells
    for (const p of placements) {
      if (p.row < 0 || p.row >= BOARD_SIZE || p.col < 0 || p.col >= BOARD_SIZE) {
        return { success: false, error: 'Placement out of bounds' };
      }
      if (this.state.board[p.row][p.col].tile !== null) {
        return { success: false, error: 'Cell already occupied' };
      }
    }

    // Validate player has the tiles
    const player = this.state.players[playerIndex];
    const rackCopy = [...player.rack];
    for (const p of placements) {
      const idx = rackCopy.findIndex(t => t.id === p.tile.id);
      if (idx === -1) {
        return { success: false, error: 'Tile not in rack' };
      }
      rackCopy.splice(idx, 1);
    }

    // Check connectivity
    const isFirstMove = this.isBoardEmpty();
    if (isFirstMove) {
      // First word must cover center
      const coversCenter = placements.some(p => p.row === 7 && p.col === 7);
      if (!coversCenter) {
        return { success: false, error: 'First word must cover the center square' };
      }
      if (placements.length < 2) {
        return { success: false, error: 'First word must be at least 2 letters' };
      }
    } else {
      // Must connect to existing tiles
      const connects = this.placementConnectsToExisting(placements);
      if (!connects) {
        return { success: false, error: 'Word must connect to existing tiles' };
      }
    }

    // Validate no gaps in the line
    if (!this.isContiguousPlacement(placements, direction)) {
      return { success: false, error: 'Tiles must form a contiguous word (no gaps unless filled by existing tiles)' };
    }

    // Temporarily place tiles on board for validation
    for (const p of placements) {
      this.state.board[p.row][p.col].tile = p.tile;
    }

    // Find all words formed
    const formedWords = this.findAllFormedWords(placements, direction);

    // Validate all words
    const invalidWords: string[] = [];
    for (const fw of formedWords) {
      if (!isValidWord(fw.word)) {
        invalidWords.push(fw.word);
      }
    }

    if (invalidWords.length > 0) {
      // Remove tiles from board
      for (const p of placements) {
        this.state.board[p.row][p.col].tile = null;
      }
      return { success: false, error: `Invalid word(s): ${invalidWords.join(', ')}` };
    }

    // Calculate score
    let totalScore = 0;
    for (const fw of formedWords) {
      totalScore += fw.score;
    }

    // Bingo bonus (all 7 tiles used)
    if (placements.length === RACK_SIZE) {
      totalScore += BINGO_BONUS;
    }

    // Update player state
    player.score += totalScore;
    player.consecutivePasses = 0;

    // Remove tiles from rack
    for (const p of placements) {
      const idx = player.rack.findIndex(t => t.id === p.tile.id);
      if (idx !== -1) {
        player.rack.splice(idx, 1);
      }
    }

    // Refill rack
    this.fillRack(player);

    // Update state
    this.state.lastPlayedWord = formedWords[0] || null;
    this.state.lastPlacedPositions = placements.map(p => ({ row: p.row, col: p.col }));
    this.state.moveHistory.push({
      playerIndex,
      type: 'play',
      words: formedWords,
      totalScore,
      timestamp: Date.now(),
    });

    // Check for game end
    if (player.rack.length === 0 && this.state.tileBag.length === 0) {
      this.endGame('empty_rack');
    } else {
      this.advanceTurn();
    }

    return { success: true, score: totalScore, words: formedWords };
  }

  /**
   * Exchange tiles - player returns tiles and draws new ones.
   */
  exchangeTiles(playerIndex: number, tileIds: string[]): { success: boolean; error?: string } {
    if (this.state.phase !== 'playing') {
      return { success: false, error: 'Game is not in progress' };
    }
    if (playerIndex !== this.state.currentPlayerIndex) {
      return { success: false, error: 'Not your turn' };
    }
    if (tileIds.length === 0) {
      return { success: false, error: 'Must select tiles to exchange' };
    }
    if (this.state.tileBag.length < tileIds.length) {
      return { success: false, error: 'Not enough tiles in bag to exchange' };
    }

    const player = this.state.players[playerIndex];
    const tilesToReturn: Tile[] = [];

    for (const id of tileIds) {
      const idx = player.rack.findIndex(t => t.id === id);
      if (idx === -1) {
        return { success: false, error: 'Tile not in rack' };
      }
      tilesToReturn.push(player.rack[idx]);
      player.rack.splice(idx, 1);
    }

    // Draw new tiles first
    this.fillRack(player);

    // Return old tiles to bag and shuffle
    this.state.tileBag.push(...tilesToReturn);
    this.state.tileBag = this.shuffleArray(this.state.tileBag);

    player.consecutivePasses = 0;
    this.state.moveHistory.push({
      playerIndex,
      type: 'exchange',
      timestamp: Date.now(),
    });

    this.advanceTurn();
    return { success: true };
  }

  /**
   * Pass turn without playing.
   */
  passTurn(playerIndex: number): { success: boolean; error?: string } {
    if (this.state.phase !== 'playing') {
      return { success: false, error: 'Game is not in progress' };
    }
    if (playerIndex !== this.state.currentPlayerIndex) {
      return { success: false, error: 'Not your turn' };
    }

    const player = this.state.players[playerIndex];
    player.consecutivePasses++;

    this.state.moveHistory.push({
      playerIndex,
      type: 'pass',
      timestamp: Date.now(),
    });

    // Check if all players have passed twice consecutively
    const allPassedTwice = this.state.players.every(p => p.consecutivePasses >= 2);
    if (allPassedTwice) {
      this.endGame('all_passed');
    } else {
      this.advanceTurn();
    }

    return { success: true };
  }

  // ==========================================================================
  // Private Helpers
  // ==========================================================================

  private getPlacementDirection(placements: TilePlacement[]): Direction | null {
    if (placements.length === 1) return 'horizontal'; // Single tile, direction doesn't matter

    const rows = new Set(placements.map(p => p.row));
    const cols = new Set(placements.map(p => p.col));

    if (rows.size === 1) return 'horizontal';
    if (cols.size === 1) return 'vertical';
    return null; // Not in a line
  }

  private isBoardEmpty(): boolean {
    for (let r = 0; r < BOARD_SIZE; r++) {
      for (let c = 0; c < BOARD_SIZE; c++) {
        if (this.state.board[r][c].tile !== null) return false;
      }
    }
    return true;
  }

  private placementConnectsToExisting(placements: TilePlacement[]): boolean {
    for (const p of placements) {
      const neighbors = [
        [p.row - 1, p.col], [p.row + 1, p.col],
        [p.row, p.col - 1], [p.row, p.col + 1],
      ];
      for (const [r, c] of neighbors) {
        if (r >= 0 && r < BOARD_SIZE && c >= 0 && c < BOARD_SIZE) {
          const cell = this.state.board[r][c];
          if (cell.tile !== null && !placements.some(pp => pp.row === r && pp.col === c)) {
            return true;
          }
        }
      }
    }
    return false;
  }

  private isContiguousPlacement(placements: TilePlacement[], direction: Direction): boolean {
    if (placements.length <= 1) return true;

    const sorted = [...placements].sort((a, b) => {
      if (direction === 'horizontal') return a.col - b.col;
      return a.row - b.row;
    });

    const fixedAxis = direction === 'horizontal' ? sorted[0].row : sorted[0].col;
    const start = direction === 'horizontal' ? sorted[0].col : sorted[0].row;
    const end = direction === 'horizontal' ? sorted[sorted.length - 1].col : sorted[sorted.length - 1].row;

    for (let i = start; i <= end; i++) {
      const r = direction === 'horizontal' ? fixedAxis : i;
      const c = direction === 'horizontal' ? i : fixedAxis;
      const isPlaced = placements.some(p => p.row === r && p.col === c);
      const hasExisting = this.state.board[r][c].tile !== null && !isPlaced;
      if (!isPlaced && !hasExisting) {
        return false;
      }
    }
    return true;
  }

  private findAllFormedWords(placements: TilePlacement[], mainDirection: Direction): PlacedWord[] {
    const words: PlacedWord[] = [];

    // Find main word along the placement direction
    const mainWord = this.extractWord(placements[0].row, placements[0].col, mainDirection, placements);
    if (mainWord && mainWord.word.length >= 2) {
      words.push(mainWord);
    }

    // Find cross words for each placed tile
    const crossDirection: Direction = mainDirection === 'horizontal' ? 'vertical' : 'horizontal';
    for (const p of placements) {
      const crossWord = this.extractWord(p.row, p.col, crossDirection, placements);
      if (crossWord && crossWord.word.length >= 2) {
        words.push(crossWord);
      }
    }

    return words;
  }

  private extractWord(row: number, col: number, direction: Direction, newPlacements: TilePlacement[]): PlacedWord | null {
    // Find the start of the word
    let r = row;
    let c = col;

    if (direction === 'horizontal') {
      while (c > 0 && this.getCellTile(r, c - 1, newPlacements)) c--;
    } else {
      while (r > 0 && this.getCellTile(r - 1, c, newPlacements)) r--;
    }

    // Build the word
    const tiles: TilePlacement[] = [];
    let word = '';
    let wordScore = 0;
    let wordMultiplier = 1;

    let cr = r;
    let cc = c;

    while (cr < BOARD_SIZE && cc < BOARD_SIZE) {
      const tile = this.getCellTile(cr, cc, newPlacements);
      if (!tile) break;

      const isNewTile = newPlacements.some(p => p.row === cr && p.col === cc);
      const cell = this.state.board[cr][cc];
      let letterScore = tile.value;

      // Premium squares only apply to newly placed tiles
      if (isNewTile) {
        switch (cell.premium) {
          case PremiumSquareType.DOUBLE_LETTER:
            letterScore *= 2;
            break;
          case PremiumSquareType.TRIPLE_LETTER:
            letterScore *= 3;
            break;
          case PremiumSquareType.DOUBLE_WORD:
          case PremiumSquareType.CENTER:
            wordMultiplier *= 2;
            break;
          case PremiumSquareType.TRIPLE_WORD:
            wordMultiplier *= 3;
            break;
        }
      }

      wordScore += letterScore;
      word += tile.isBlank ? (tile.letter || '?') : tile.letter;
      tiles.push({ tile, row: cr, col: cc });

      if (direction === 'horizontal') cc++;
      else cr++;
    }

    if (word.length < 2) return null;

    return {
      word: word.toLowerCase(),
      score: wordScore * wordMultiplier,
      tiles,
      direction,
    };
  }

  private getCellTile(row: number, col: number, newPlacements: TilePlacement[]): Tile | null {
    // Check new placements first
    const placed = newPlacements.find(p => p.row === row && p.col === col);
    if (placed) return placed.tile;
    // Then check board
    return this.state.board[row][col].tile;
  }

  private advanceTurn(): void {
    this.state.currentPlayerIndex = (this.state.currentPlayerIndex + 1) % this.state.players.length;
    this.state.turnNumber++;
  }

  private endGame(reason: string): void {
    this.state.phase = 'ended';

    // Calculate final scores
    if (reason === 'empty_rack') {
      // Player who emptied rack gets sum of all other players remaining tile values
      const emptyPlayer = this.state.players[this.state.currentPlayerIndex];
      let bonus = 0;
      for (const player of this.state.players) {
        if (player.id !== emptyPlayer.id) {
          const remaining = player.rack.reduce((sum, t) => sum + t.value, 0);
          player.score -= remaining;
          bonus += remaining;
        }
      }
      emptyPlayer.score += bonus;
    } else {
      // All players lose remaining tile values
      for (const player of this.state.players) {
        const remaining = player.rack.reduce((sum, t) => sum + t.value, 0);
        player.score -= remaining;
      }
    }

    // Determine winner
    let maxScore = -Infinity;
    let winnerId: string | null = null;
    for (const player of this.state.players) {
      if (player.score > maxScore) {
        maxScore = player.score;
        winnerId = player.id;
      }
    }
    this.state.winner = winnerId;
    this.state.gameOverReason = reason === 'empty_rack'
      ? 'A player used all their tiles'
      : 'All players passed consecutively';
  }

  // ==========================================================================
  // Public Getters
  // ==========================================================================

  getState(): WordGameState {
    return this.state;
  }

  getBoard(): BoardCell[][] {
    return this.state.board;
  }

  getCurrentPlayer(): WordGamePlayer | null {
    if (this.state.players.length === 0) return null;
    return this.state.players[this.state.currentPlayerIndex];
  }

  getTileBagCount(): number {
    return this.state.tileBag.length;
  }

  isGameOver(): boolean {
    return this.state.phase === 'ended';
  }

  getScores(): { name: string; score: number }[] {
    return this.state.players.map(p => ({ name: p.name, score: p.score }));
  }

  /** Get premium square type for a position */
  static getPremiumSquareType(row: number, col: number): PremiumSquareType {
    if (row === 7 && col === 7) return PremiumSquareType.CENTER;
    if (TRIPLE_WORD_POSITIONS.some(([r, c]) => r === row && c === col)) return PremiumSquareType.TRIPLE_WORD;
    if (DOUBLE_WORD_POSITIONS.some(([r, c]) => r === row && c === col)) return PremiumSquareType.DOUBLE_WORD;
    if (TRIPLE_LETTER_POSITIONS.some(([r, c]) => r === row && c === col)) return PremiumSquareType.TRIPLE_LETTER;
    if (DOUBLE_LETTER_POSITIONS.some(([r, c]) => r === row && c === col)) return PremiumSquareType.DOUBLE_LETTER;
    return PremiumSquareType.NONE;
  }

  /** Get the letter value for scoring */
  static getLetterValue(letter: string): number {
    const entry = TILE_DISTRIBUTION.find(([l]) => l === letter.toUpperCase());
    return entry ? entry[1] : 0;
  }
}

export default WordGameEngine;
